import tokenManager from '../identity/TokenManager'
import { logEnvioHistorial } from './WANotificationsServices'

// ─────────────────────────────────────────────────────────────────────────────
// Pipeline de correo ON-DEMAND para flujos Apoderado/Legal (RP-35).
//
// Arquitectura (análoga al deflector de WhatsApp, evita el polling legacy):
//   frontend callEmail(codigo, email, ...vars)
//     → proxy EI WSO2 EnviarCorreoProxy  (docs/wso2/EnviarCorreoProxy.xml, QA 2026-05-28)
//         → dblookup mensaje_notificar(codigo)  (docs/sql/representantes-correo-templates-2026-05-28.sql)
//         → sustituye $1..$4 → POST a enviamail_generico.php (PHPMailer/Office365)
//
// El proxy es el MISMO contrato JSON {op_enviar_correo:{email,codigo_mensaje,var1..var4}}.
// callEmail lo alcanza por el MISMO proxy CORS que token_clave / log_envio (sin Bearer:
// el proxy EI no exige auth). La URL exacta del proxy EI se configura en VITE_CORREO_URL.
//
// Gating: se activa solo si VITE_CORREO_URL está definida (ver .env.qa). Si queda vacía,
// callEmail devuelve false sin tocar la red y los call-sites siguen funcionando solo por
// WhatsApp (el correo es COMPLEMENTARIO, nunca bloquea el flujo). Mismo patrón que
// VITE_TOKEN_CLAVE_URL / VITE_LOG_ENVIOS_URL.
//
// Mapeo codigo_mensaje (correo) ← template WhatsApp del MISMO flujo Apoderado/Legal.
// El backend de correo (tabla mensaje_notificar, códigos 8..16) se construyó sobre la
// numeración WA legacy (82..90 → 8..16). Tras el resync G17 los call-sites usan los ids
// FINALES del Sheet templates_vfinal_v1 (95/96/99..103), por lo que mapeamos esos ids al
// MISMO codigo_mensaje del backend (el código no cambia, solo la clave WA de origen):
//   95→8 (armador designa) · 96→9 (admin designa) · 103→11 (armador: apoderado aprobado) ·
//   102→12 (armador: apoderado rechazado) · 101→13 (removido por armador, al apoderado) ·
//   100→14 (removido por admin, al apoderado) · 99→15 (armador: aviso de remoción).
// El ORDEN de `vars` coincide EXACTO con el de var_body_N del WA equivalente, así el
// call-site pasa el MISMO arreglo a callNotification (WA) y a callEmail (correo).
// ─────────────────────────────────────────────────────────────────────────────

const CORREO_URL = import.meta.env.VITE_CORREO_URL || ''
const QA_OVERRIDE_EMAIL: string | null = null // Correo para set de pruebas (null = enviar al correo real del destinatario)

/** True si el pipeline de correo on-demand está habilitado (URL configurada para el entorno). */
export function correoHabilitado(): boolean {
  return !!CORREO_URL
}

/** codigo_mensaje de correo a partir del template_id de WhatsApp FINAL (Sheet) del mismo flujo. */
export enum CodigoMensajeEmail {
  CREACION_USUARIO_ADMIN = 101, // Mensaje 1: Enlace de creación de clave al crear usuario por admin
  RECUPERACION_CLAVE = 102, // Mensaje 2: Recuperación de clave
  AVISO_ADMIN_NUEVA_SOLICITUD = 103, // Mensaje 3: Aviso al admin de nueva solicitud/modificación
  APROBACION_AO = 104, // Mensaje 4: Aprobación de Autorización de Operación (AO)
  AVISO_PREVENTIVO_AO_ADMIN = 105, // Mensaje 5: Alerta 7 días antes de caducidad AO al admin
  AVISO_CADUCIDAD_AO_ADMIN = 106, // Mensaje 6: Notificación de caducidad AO al admin
  MODIFICACION_VIGENCIA_AO = 107, // Mensaje 7: Modificación manual de fecha de vigencia AO
  APROBACION_MODIFICACION_EMB = 108, // Mensaje 8: Aprobación de modificación de datos de nave con AO
  RECHAZO_MODIFICACION_EMB = 109, // Mensaje 9: Rechazo de modificación de datos de nave con AO
  SUSPENSION_AO_CN_VENCIDO = 110, // Mensaje 10: Suspensión automática de AO por CN vencido
  AVISO_PREVENTIVO_CN_ARMADOR = 111, // Mensaje 11: Alerta 7 días antes de vencimiento de CN al armador
  AVISO_PREVENTIVO_CN_ADMIN = 112, // Mensaje 12: Alerta 7 días antes de vencimiento de CN al admin
  LEVANTAMIENTO_SUSPENSION_AO = 113, // Mensaje 13: Levantamiento de suspensión / Restablecimiento AO
  APROBACION_TRIPULANTE = 114, // Mensaje 14: Aprobación de tripulante
  RECHAZO_TRIPULANTE = 115, // Mensaje 15: Rechazo de tripulante con motivo
  REHABILITACION_TRIPULANTE = 116, // Mensaje 16: Rehabilitación de tripulante
  TOMA_CONOCIMIENTO_REGLAMENTO = 117, // Mensaje 17: Toma de conocimiento obligatoria para AO por admin
}

const GENERICO_URL = 'https://cors.biznet.cl:8080/http://muelleprat.biznet.cl/websrv/sw/enviamail_generico.php'

const EMAIL_TEMPLATES_100: Record<number, (vars: string[]) => { asunto: string; mensaje: string }> = {
  // Mensaje 1: Creación de cuenta (PP-RA-07, PP-TX-04)
  [CodigoMensajeEmail.CREACION_USUARIO_ADMIN]: (vars) => ({
    asunto: 'Cuenta creada exitosamente',
    mensaje: `Estimado/a ${
      vars[0] || 'usuario'
    }:<br><br>Su cuenta en el Sistema de Gestión Muelle Prat ha sido creada exitosamente.<br><br>Para crear su contraseña y activar el acceso, ingrese al siguiente enlace:<br><br><a href="${
      vars[1] || '#'
    }" target="_blank" style="display:inline-block;padding:8px 16px;background-color:#0284c7;color:#ffffff;text-decoration:none;border-radius:4px;font-weight:bold;">Crear contraseña</a><br><br>Una vez creada, podrá acceder a los módulos habilitados para su perfil.<br><br>Si no reconoce la creación de esta cuenta o presenta dificultades para acceder, comuníquese con nosotros al correo <a href="mailto:muelleprat@puertovalparaiso.cl">muelleprat@puertovalparaiso.cl</a><br><br>Saludos,<br>Empresa Portuaria Valparaíso`,
  }),

  // Mensaje 2: Recuperación de contraseña (PP-TX-05)
  [CodigoMensajeEmail.RECUPERACION_CLAVE]: (vars) => ({
    asunto: 'RECUPERACIÓN DE CONTRASEÑA',
    mensaje: `Estimado/a ${
      vars[0] || 'usuario'
    }:<br><br>Hemos recibido una solicitud para recuperar su contraseña del Sistema de Gestión Muelle Prat.<br><br>Para crear una nueva contraseña, ingrese al siguiente enlace:<br><br><a href="${
      vars[1] || '#'
    }" target="_blank" style="display:inline-block;padding:8px 16px;background-color:#0284c7;color:#ffffff;text-decoration:none;border-radius:4px;font-weight:bold;">Restablecer contraseña</a><br><br>Si no realizó esta solicitud, puede ignorar este correo.<br><br>Saludos,<br>Empresa Portuaria Valparaíso`,
  }),

  // Mensaje 3: Solicitud pendiente de evaluación (PP-AO-01, PP-AO-12, PP-AO-17, PP-TR-01, PP-TR-05, PP-TR-10, PP-TR-16, PP-TX-13)
  [CodigoMensajeEmail.AVISO_ADMIN_NUEVA_SOLICITUD]: (vars) => ({
    asunto: 'Solicitud pendiente de evaluación',
    mensaje: `Administrador:<br><br>Se ha recibido una solicitud de ${
      vars[0] || 'operación'
    } que requiere su evaluación y resolución.<br><br>Saludos`,
  }),

  // Mensaje 4: Aprobación de AO (PP-AO-02)
  [CodigoMensajeEmail.APROBACION_AO]: (vars) => ({
    asunto: 'Aprobación de Autorización de Operación',
    mensaje: `Estimado/a ${
      vars[0] || 'armador o representante legal'
    }:<br><br>Le informamos que la solicitud de Autorización de Operación de la embarcación <strong>${
      vars[1] || ''
    }</strong> ha sido aprobada.<br><br>Datos de la autorización:<br><br><strong>Estado:</strong> Vigente<br><strong>Fecha de inicio:</strong> ${
      vars[2] || ''
    }<br><strong>Fecha de vencimiento:</strong> ${
      vars[3] || ''
    }<br><br>El documento de autorización se encuentra disponible para descarga en el Sistema.<br><br>Saludos<br>Empresa Portuaria Valparaíso`,
  }),

  // Mensaje 5: AO próxima a caducar (PP-AO-08, PP-AO-09)
  [CodigoMensajeEmail.AVISO_PREVENTIVO_AO_ADMIN]: (vars) => ({
    asunto: 'Caducidad de Autorización de Operación',
    mensaje: `Estimado/a administrador:<br><br>La Autorización de Operación de la embarcación <strong>${
      vars[0] || ''
    }</strong> caducará el <strong>${
      vars[1] || ''
    }</strong>.<br><br>Se solicita revisar el caso y realizar las gestiones correspondientes antes de su vencimiento.<br><br>Saludos,<br>Empresa Portuaria Valparaíso`,
  }),

  // Mensaje 6: AO caducada (PP-AO-08, PP-AO-09)
  [CodigoMensajeEmail.AVISO_CADUCIDAD_AO_ADMIN]: (vars) => ({
    asunto: 'Caducidad de Autorización de Operación',
    mensaje: `Estimado/a administrador:<br><br>Se informa que la Autorización de Operación de la embarcación <strong>${
      vars[0] || ''
    }</strong> ha pasado automáticamente al estado Caducada, debido al vencimiento de su plazo de vigencia.<br><br><strong>Armador:</strong> ${
      vars[1] || ''
    }<br><strong>Fecha de vencimiento:</strong> ${vars[2] || ''}<br><br>Saludos`,
  }),

  // Mensaje 7: Modificación de vigencia de AO (PP-AO-10)
  [CodigoMensajeEmail.MODIFICACION_VIGENCIA_AO]: (vars) => {
    const nombreArmador = vars[0] || 'armador o representante legal'
    const nombreNave = vars[1] || ''
    const estado = vars.length >= 6 ? vars[2] : 'Vigente'
    const fechaAnt = vars.length >= 6 ? vars[3] : 'No registrada'
    const nuevaFecha = vars.length >= 6 ? vars[4] : vars[2] || ''
    const motivo = vars.length >= 6 ? vars[5] : vars[3] || 'Modificación administrativa'

    return {
      asunto: 'Modificación de vigencia de Autorización de Operación',
      mensaje: `Estimado/a ${nombreArmador}:<br><br>Le informamos que se ha modificado el plazo de vigencia de la Autorización de Operación de la embarcación <strong>${nombreNave}</strong>.<br><br>Datos de la modificación:<br><br><strong>Estado actual de la AO:</strong> ${estado}<br><strong>Fecha de vencimiento anterior:</strong> ${fechaAnt}<br><strong>Nueva fecha de vencimiento:</strong> ${nuevaFecha}<br><strong>Motivo del cambio:</strong> ${motivo}<br><br>Esta modificación no altera las demás condiciones ni el estado actual de la autorización.<br><br>Puede revisar los datos actualizados en el Sistema.<br><br>Si requiere más información, comuníquese con nosotros al correo <a href="mailto:muelleprat@puertovalparaiso.cl">muelleprat@puertovalparaiso.cl</a>.<br><br>Saludos<br>Empresa Portuaria Valparaíso`,
    }
  },

  // Mensaje 8: Modificación de datos aprobada (PP-AO-12)
  [CodigoMensajeEmail.APROBACION_MODIFICACION_EMB]: (vars) => ({
    asunto: 'Solicitud de modificación aprobada',
    mensaje: `Estimado/a ${
      vars[0] || 'armador o representante legal'
    }:<br><br>Le informamos que su solicitud de modificación de datos ha sido aprobada por Puerto Valparaíso.<br><br>Los cambios aprobados ya se encuentran aplicados en el Sistema.<br><br>Si requiere más información, comuníquese con nosotros al correo <a href="mailto:muelleprat@puertovalparaiso.cl">muelleprat@puertovalparaiso.cl</a>.<br><br>Saludos<br>Empresa Portuaria Valparaíso`,
  }),

  // Mensaje 9: Modificación de datos rechazada (PP-AO-12)
  [CodigoMensajeEmail.RECHAZO_MODIFICACION_EMB]: (vars) => ({
    asunto: 'Solicitud de modificación rechazada',
    mensaje: `Estimado/a ${
      vars[0] || 'armador o representante legal'
    }:<br><br>Le informamos que su solicitud de modificación de datos ha sido rechazada por Puerto Valparaíso.<br><br>Si requiere más información, comuníquese con nosotros al correo <a href="mailto:muelleprat@puertovalparaiso.cl">muelleprat@puertovalparaiso.cl</a>.<br><br>Saludos<br>Empresa Portuaria Valparaíso`,
  }),

  // Mensaje 10: Suspensión automática por CN vencido (PP-AO-15)
  [CodigoMensajeEmail.SUSPENSION_AO_CN_VENCIDO]: (vars) => ({
    asunto: 'Suspensión de Autorización de Operación por vencimiento del Certificado de Navegabilidad',
    mensaje: `Estimado/a ${
      vars[0] || 'armador o representante legal'
    }:<br><br>Le informamos que la Autorización de Operación de la embarcación <strong>${
      vars[1] || ''
    }</strong> ha sido suspendida automáticamente debido al vencimiento de su Certificado de Navegabilidad.<br><br><strong>Estado de la autorización:</strong> Suspendida<br><strong>Fecha de vencimiento del certificado:</strong> ${
      vars[2] || ''
    }<br><br>Mientras la autorización permanezca suspendida, la embarcación no podrá operar en Muelle Prat.<br><br>Para revisar los antecedentes y gestionar la actualización del certificado, ingrese a <a href="https://muelleprat.puertovalparaiso.cl">www.muelleprat.puertovalparaiso.cl</a><br><br>Si requiere más información, comuníquese con nosotros al correo <a href="mailto:muelleprat@puertovalparaiso.cl">muelleprat@puertovalparaiso.cl</a><br><br>Saludos<br>Empresa Portuaria Valparaíso`,
  }),

  // Mensaje 11: Aviso de próximo vencimiento del Certificado de Navegabilidad al armador (PP-AO-16)
  [CodigoMensajeEmail.AVISO_PREVENTIVO_CN_ARMADOR]: (vars) => ({
    asunto: 'Aviso de próximo vencimiento del Certificado de Navegabilidad',
    mensaje: `Estimado/a ${
      vars[0] || 'armador o representante legal'
    }:<br><br>Le informamos que el Certificado de Navegabilidad de la embarcación <strong>${
      vars[1] || ''
    }</strong> vencerá el <strong>${
      vars[2] || ''
    }</strong>.<br><br>Le solicitamos gestionar oportunamente su renovación y actualizar el documento en el Sistema.<br><br>Si el certificado vence sin ser actualizado, la Autorización de Operación de la embarcación será suspendida automáticamente y esta no podrá operar en Muelle Prat.<br><br>Si requiere más información, comuníquese con nosotros al correo <a href="mailto:muelleprat@puertovalparaiso.cl">muelleprat@puertovalparaiso.cl</a>.<br><br>Saludos,<br>Empresa Portuaria Valparaíso`,
  }),

  // Mensaje 12: Certificado de Navegabilidad próximo a vencer al administrador (PP-AO-16)
  [CodigoMensajeEmail.AVISO_PREVENTIVO_CN_ADMIN]: (vars) => ({
    asunto: 'Certificado de Navegabilidad próximo a vencer',
    mensaje: `Estimado/a Administrador:<br><br>El Certificado de Navegabilidad de la embarcación <strong>${
      vars[0] || ''
    }</strong> vencerá el <strong>${
      vars[1] || ''
    }</strong>.<br><br>Se solicita revisar el caso y realizar las gestiones correspondientes antes de su vencimiento.<br><br>Saludos`,
  }),

  // Mensaje 13: Reactivación de la AO después de actualizar el CN (PP-AO-07, PP-AO-17)
  [CodigoMensajeEmail.LEVANTAMIENTO_SUSPENSION_AO]: (vars) => ({
    asunto: 'Autorización de Operación vigente',
    mensaje: `Estimado/a ${
      vars[0] || 'armador o representante legal'
    }:<br><br>La Autorización de Operación de la embarcación <strong>${
      vars[1] || ''
    }</strong> se encuentra nuevamente vigente y la embarcación puede volver a operar en Muelle Prat.<br><br>Saludos,<br>Empresa Portuaria Valparaíso`,
  }),

  // Mensaje 14: Aprobación de tripulante (PP-TR-03, PP-TR-10)
  [CodigoMensajeEmail.APROBACION_TRIPULANTE]: (vars) => ({
    asunto: 'Tripulante aprobado',
    mensaje: `Estimado/a ${
      vars[0] || 'armador o representante legal'
    }:<br><br>Le informamos que la inscripción del tripulante <strong>${
      vars[1] || ''
    }</strong> ha sido aprobada.<br><br>El tripulante se encuentra habilitado para operar, siempre que esté incluido en una Autorización de Zarpe vigente.<br><br>Si requiere más información, comuníquese con nosotros al correo <a href="mailto:muelleprat@puertovalparaiso.cl">muelleprat@puertovalparaiso.cl</a>.<br><br>Saludos,<br>Empresa Portuaria Valparaíso`,
  }),

  // Mensaje 15: Rechazo de tripulante (PP-TR-04, PP-TR-10)
  [CodigoMensajeEmail.RECHAZO_TRIPULANTE]: (vars) => ({
    asunto: 'Inscripción de tripulante rechazada',
    mensaje: `Estimado/a ${
      vars[0] || 'armador o representante legal'
    }:<br><br>Le informamos que la inscripción del tripulante <strong>${
      vars[1] || ''
    }</strong> ha sido rechazada.<br><br><strong>Motivo:</strong> ${
      vars[2] || ''
    }<br><br>El tripulante no se encuentra habilitado para operar. Podrá solicitar nuevamente su inscripción una vez subsanado el motivo del rechazo si correspondiese.<br><br>Si requiere más información, comuníquese con nosotros al correo <a href="mailto:muelleprat@puertovalparaiso.cl">muelleprat@puertovalparaiso.cl</a>.<br><br>Saludos,<br>Empresa Portuaria Valparaíso`,
  }),

  // Mensaje 16: Rehabilitación de tripulante (PP-TR-07)
  [CodigoMensajeEmail.REHABILITACION_TRIPULANTE]: (vars) => ({
    asunto: 'Tripulante habilitado',
    mensaje: `Estimado/a ${
      vars[0] || 'armador o representante legal'
    }:<br><br>Le informamos que el tripulante <strong>${
      vars[1] || ''
    }</strong> ha sido habilitado nuevamente en el Sistema.<br><br>El tripulante puede volver a operar, siempre que esté incluido en una Autorización de Zarpe vigente.<br><br>Si requiere más información, comuníquese con nosotros al correo <a href="mailto:muelleprat@puertovalparaiso.cl">muelleprat@puertovalparaiso.cl</a>.<br><br>Saludos<br>Empresa Portuaria Valparaíso`,
  }),

  // Mensaje 17: Toma de conocimiento del Reglamento de Operación (PP-TX-11)
  [CodigoMensajeEmail.TOMA_CONOCIMIENTO_REGLAMENTO]: (vars) => ({
    asunto: 'Acción requerida para solicitar Autorización de Operación',
    mensaje: `Estimado/a ${
      vars[0] || 'armador o representante legal'
    }:<br><br>Se ha iniciado una solicitud de Autorización de Operación para la embarcación <strong>${
      vars[1] || ''
    }</strong>.<br><br>Para enviar la solicitud a evaluación, debe revisar el Reglamento de Operación y declarar que ha tomado conocimiento y acepta sus condiciones.<br><br><a href="${
      vars[2] || '#'
    }" target="_blank" style="display:inline-block;padding:8px 16px;background-color:#0284c7;color:#ffffff;text-decoration:none;border-radius:4px;font-weight:bold;">Revisar Reglamento de Operación</a><br><br>Una vez realizada esta acción, la solicitud será enviada a evaluación.<br><br>Si requiere más información, comuníquese con nosotros al correo <a href="mailto:muelleprat@puertovalparaiso.cl">muelleprat@puertovalparaiso.cl</a>.<br><br>Saludos,<br>Empresa Portuaria Valparaíso`,
  }),
}

export function codigoCorreoDeWA(waTemplateId: number): number {
  const mapa: Record<number, number> = { 95: 8, 96: 9, 103: 11, 102: 12, 101: 13, 100: 14, 99: 15 }
  return mapa[waTemplateId] ?? waTemplateId - 74
}

class EmailNotificationsServices {
  constructor() {}

  public startup() {}

  async recuperarContraseña(correo: string): Promise<boolean> {
    try {
      const token = await tokenManager.getToken()
      const body = {
        op_recuperar_clave: {
          email: correo,
        },
      }

      const response = await fetch(`${import.meta.env.VITE_BASE_URL}/login_rol_menu/1/op_recuperar_clave`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      })

      const data = await response.json()

      return true
    } catch (err) {
      console.error('Error sending notification', err)
    }
    return false
  }

  /**
   * Envía un correo con asunto y cuerpo HTML personalizados directamente vía enviamail_generico.php.
   */
  async sendDirectEmail(destinatario: string | null | undefined, asunto: string, mensajeHtml: string): Promise<boolean> {
    const targetEmail = QA_OVERRIDE_EMAIL || destinatario
    if (!targetEmail || !targetEmail.trim()) {
      return false
    }
    try {
      const params = new URLSearchParams()
      params.append('asunto', asunto)
      params.append('mensaje', mensajeHtml)
      params.append('email', targetEmail.trim())

      const response = await fetch(GENERICO_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: params.toString(),
      })
      const data = await response.json()
      const ok = data && data.status === 'sent'
      logEnvioHistorial({
        canal: 'correo',
        receptor: targetEmail.trim(),
        resultado: ok ? 'enviado' : 'error',
        template_id: 999,
        variables: [asunto],
        respuesta: data,
      })
      return !!ok
    } catch (err) {
      console.error('sendDirectEmail error:', err)
      return false
    }
  }

  /**
   * Envío de correo on-demand para un flujo Apoderado/Legal. Resuelve la plantilla por
   * `codigoMensaje` (tabla mensaje_notificar) y sustituye las variables posicionales $1..$4
   * en el backend EI. Es COMPLEMENTARIO al WhatsApp y best-effort: no lanza, registra el
   * intento en el historial interno y devuelve false ante cualquier fallo o si está deshabilitado.
   *
   * @param codigoMensaje  codigo_mensaje en mensaje_notificar (8..16). Usa codigoCorreoDeWA() para derivarlo del WA id.
   * @param email          correo del destinatario. Si falta, se omite (no todos los apoderados tienen correo).
   * @param vars           variables posicionales, MISMO orden que el WA equivalente (máx 4).
   */
  async callEmail(codigoMensaje: number, email: string | null | undefined, ...vars: string[]): Promise<boolean> {
    const targetEmail = QA_OVERRIDE_EMAIL || email
    if (!CORREO_URL) {
      console.log(`[QA Correo] Código: ${codigoMensaje} → ${targetEmail} | Vars:`, vars)
    }
    if (!targetEmail || !targetEmail.trim()) {
      logEnvioHistorial({
        canal: 'correo',
        receptor: email ?? '',
        resultado: 'omitido',
        template_id: codigoMensaje,
        variables: vars,
        contexto: 'sin correo del destinatario',
      })
      return false
    }

    const destinatario = targetEmail.trim()

    // Si tenemos la plantilla exacta (Mensajes 1 al 17, o 101 al 117), enviamos con asunto y cuerpo HTML exactos
    const templateBuilder =
      EMAIL_TEMPLATES_100[codigoMensaje] ||
      (codigoMensaje <= 20 ? EMAIL_TEMPLATES_100[codigoMensaje + 100] : EMAIL_TEMPLATES_100[codigoMensaje - 100])
    if (templateBuilder) {
      try {
        const { asunto, mensaje } = templateBuilder(vars)
        const params = new URLSearchParams()
        params.append('asunto', asunto)
        params.append('mensaje', mensaje)
        params.append('email', destinatario)

        const response = await fetch(GENERICO_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: params.toString(),
        })
        const data = await response.json()
        const ok = data && data.status === 'sent'

        logEnvioHistorial({
          canal: 'correo',
          receptor: destinatario,
          resultado: ok ? 'enviado' : 'error',
          template_id: codigoMensaje,
          variables: vars,
          respuesta: data,
        })

        return !!ok
      } catch (err) {
        console.error('callEmail direct template error:', err)
      }
    }

    const body = {
      op_enviar_correo: {
        email: destinatario,
        codigo_mensaje: codigoMensaje,
        var1: vars[0] ?? '',
        var2: vars[1] ?? '',
        var3: vars[2] ?? '',
        var4: vars[3] ?? '',
      },
    }

    try {
      let response = await fetch(CORREO_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-requested-with': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      })
      let data = await response.json()
      let ok = data && data.status === 'sent'

      // Fallback para ambiente QA si el código > 100 aún no está insertado en la BD remota
      if (!ok && codigoMensaje >= 100) {
        console.warn(
          `[QA Correo] Código ${codigoMensaje} no encontrado en BD. Reintentando con plantilla activa de fallback (11)...`,
        )
        body.op_enviar_correo.codigo_mensaje = 11
        response = await fetch(CORREO_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-requested-with': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
        data = await response.json()
        ok = data && data.status === 'sent'
      }

      logEnvioHistorial({
        canal: 'correo',
        receptor: destinatario,
        resultado: ok ? 'enviado' : 'error',
        template_id: codigoMensaje,
        variables: vars,
        respuesta: data,
      })

      return !!ok
    } catch (err) {
      console.error('callEmail: error de red', err)
      logEnvioHistorial({
        canal: 'correo',
        receptor: destinatario,
        resultado: 'error',
        template_id: codigoMensaje,
        variables: vars,
        respuesta: String(err),
      })
    }
    return false
  }
}

const emailNotificationsManager = new EmailNotificationsServices()

export default emailNotificationsManager
